import * as React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import { Button, Text, tokens, Tooltip } from '@hotjar/hotsauce';
import { useSettings } from '@hotjar/webapp/hooks/useSettings';
import { prettifyPriceWithCurrencySymbol } from '@hotjar/webapp/billing/cost';
import type { Plan } from '@hotjar/webapp/domains/revenue/plan/types';
import type { AccountDTO as Account } from '@hotjar/account';

export interface PlansCostChangeProps {
  changedPlans: Array<{
    id: string;
    plan: Plan;
  }>;
  changedPlansCost: {
    total: string;
    subtotal: string;
    yearlyDiscount?: {
      id: string;
      name: string;
      application: string;
      amount: string;
      percentage: string;
    };
    discounted: boolean;
  };
  account: Pick<Account, 'currency' | 'billing_cycle'>;
  updateSubscription: () => void;
  cancelPlanChanges: () => void;
  submitting: boolean;
  isDisabled?: boolean;
}

const FadeInSlideUpAnimation = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  100% {
    opacity: 1;
    transform: translateY(0px);
  }
`;

const SlideUpFadeIn = styled.div`
  position: fixed;
  bottom: 0;
  width: 780px;
  animation: ${FadeInSlideUpAnimation} 0.3s forwards;
`;

const PlansCostContainer = styled.div`
  z-index: 980; /* Below the z-index of the dropdowns which are 1000 */
  display: block;
  padding: ${tokens.spacer.x4};
  border-radius: ${tokens.radius.m} ${tokens.radius.m} 0 0;
  background: ${tokens.color.bg_active};
  text-align: center;
  ${tokens.elevation(1)}
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Col = styled.div`
  margin: 0 ${tokens.spacer.x2};
`;

const PriceBreakdown = styled.div`
  text-align: left;
`;

const DiscountedCostContainer = styled.div`
  border-bottom: 1px dashed ${tokens.color.text_light};
`;

const TotalCostTitle = styled(Text)`
  display: block;
  margin-bottom: ${tokens.spacer.x0};
`;

const SubTotal = styled(Text)`
  text-decoration: line-through;
  margin-left: ${tokens.spacer.x0};
`;

export const PlansCostChange = ({
  changedPlans,
  changedPlansCost,
  account,
  updateSubscription,
  cancelPlanChanges,
  submitting,
  isDisabled = false,
}: PlansCostChangeProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const { data: settings } = useSettings();

  const currencySymbols = settings?.currency_symbols;

  if (!changedPlans || changedPlans.length === 0 || !changedPlansCost || !account.currency) {
    return null;
  }

  const currencySymbol = currencySymbols?.[account?.currency];

  const tooltipTextCalculation = () => {
    if (
      account.billing_cycle !== 'yearly_recurring' ||
      !changedPlansCost.yearlyDiscount ||
      !currencySymbol
    ) {
      return '';
    }
    const {
      total,
      subtotal,
      yearlyDiscount: { amount, percentage },
    } = changedPlansCost;

    return t('billing:plans-cost-change.tooltip-text', {
      subtotal,
      discountedSubtotal: prettifyPriceWithCurrencySymbol(currencySymbol)(amount),
      percentage,
      total,
    });
  };

  return (
    <SlideUpFadeIn>
      <PlansCostContainer>
        <Row>
          <Col>
            <Text as="h5" textType="header5">
              {t('billing:plans-cost-change.plans-changed', { count: changedPlans.length })}
            </Text>
          </Col>
          <Col>
            <PriceBreakdown>
              <TotalCostTitle textType="caption">
                {t(
                  `billing:plans-cost-change.total-${
                    account.billing_cycle === 'yearly_recurring' ? 'yearly' : 'monthly'
                  }-cost`,
                )}
              </TotalCostTitle>

              {!changedPlansCost.discounted && (
                <Text as="span" textType="body1">
                  {changedPlansCost.total}
                </Text>
              )}

              {changedPlansCost.discounted && (
                <Tooltip
                  maxWidth={200}
                  placement="top"
                  text={`${tooltipTextCalculation()}`}
                  className="tooltip-text"
                >
                  <DiscountedCostContainer>
                    <Text as="span" textType="body1">
                      {changedPlansCost.total}
                    </Text>
                    <SubTotal textType="caption" textColor="text_light">
                      {changedPlansCost.subtotal}
                    </SubTotal>
                  </DiscountedCostContainer>
                </Tooltip>
              )}
            </PriceBreakdown>
          </Col>
          <Col>
            <Button
              onClick={() => updateSubscription()}
              isDisabled={isDisabled || submitting}
              isLoading={submitting}
              loadingText={t('billing:plans-cost-change.action-updating')}
            >
              {t('billing:plans-cost-change.action-update')}
            </Button>
          </Col>
          <Col>
            <Button as="a" kind="ghost" onClick={() => cancelPlanChanges()}>
              {t('billing:plans-cost-change.action-cancel')}
            </Button>
          </Col>
        </Row>
      </PlansCostContainer>
    </SlideUpFadeIn>
  );
};
